
import { defineComponent } from 'vue'
import { ElMessage } from 'element-plus'
import user from '@/utils/user'
import {
    queryIsCollectHandlerInfo,
    fillSigners,
    queryUnsignedContract,
    QueryIsCollectHandlerInfoResDto,
    QueryUnsignedContractResDto
} from '@/api/esign-contract'

// 存储key前缀
const STORAGE_KEY_PREFIX = 'esign_contract_ignored_'
// 防抖间隔
const REFRESH_INTERVAL = 5000

// 封装的消息提示方法，设置更高的 zIndex
const showMessage = {
    success: (message: string) => ElMessage({ message, type: 'success', zIndex: 3000 }),
    warning: (message: string) => ElMessage({ message, type: 'warning', zIndex: 3000 }),
    error: (message: string) => ElMessage({ message, type: 'error', zIndex: 3000 }),
    info: (message: string) => ElMessage({ message, type: 'info', zIndex: 3000 })
}

// 获取今天的日期字符串
const getTodayStr = (): string => {
    const now = new Date()
    return `${now.getFullYear()}-${now.getMonth() + 1}-${now.getDate()}`
}

// 检查今天是否已经忽略过（区分公司和用户）
const isIgnoredToday = (productType: number): boolean => {
    const companyId = user.companyId || ''
    const userId = user.userId || ''
    const key = `${STORAGE_KEY_PREFIX}${companyId}_${userId}_${productType}`
    const storedDate = localStorage.getItem(key)
    return storedDate === getTodayStr()
}

// 设置今天已忽略（区分公司和用户）
const setIgnoredToday = (productType: number): void => {
    const companyId = user.companyId || ''
    const userId = user.userId || ''
    const key = `${STORAGE_KEY_PREFIX}${companyId}_${userId}_${productType}`
    localStorage.setItem(key, getTodayStr())
}

export interface EsignContractDialogExpose {
    checkAndShowDialog: (productType: number) => Promise<boolean>
    closeDialogSilently: () => void
}

export default defineComponent({
    name: 'EsignContractDialog',
    data() {
        return {
            dialogVisible: false,
            currentStep: 1 as 1 | 2,
            productType: 0,
            days: 0,
            signerName: '',
            signUrl: '',
            fileName: '',
            lastRefreshTime: 0,
            resolvePromise: null as ((value: boolean) => void) | null,
            form: {
                name: '',
                phone: ''
            }
        }
    },
    computed: {
        isSupPcAdmin(): boolean {
            return user.isSupPcAdmin
        },
        companyName(): string {
            return user.companyName
        },
        canClose(): boolean {
            return this.days !== 0
        }
    },
    methods: {
        // 重置表单
        resetForm() {
            this.form.name = ''
            this.form.phone = ''
            this.currentStep = 1
            this.signerName = ''
            this.signUrl = ''
            this.fileName = ''
        },

        // 复制链接
        async copyLink() {
            if (!this.signUrl) {
                showMessage.warning('暂无签署链接')
                return
            }
            try {
                await navigator.clipboard.writeText(this.signUrl)
                showMessage.success('链接已复制')
            } catch {
                showMessage.error('复制失败，请手动复制')
            }
        },

        // 处理关闭按钮
        handleClose() {
            if (this.canClose) {
                setIgnoredToday(this.productType)
                this.dialogVisible = false
                this.resolvePromise?.(true)
                this.resetForm()
            }
        },

        // 处理忽略按钮
        handleIgnore() {
            if (this.canClose) {
                setIgnoredToday(this.productType)
                this.dialogVisible = false
                this.resolvePromise?.(true)
            }
        },

        // 处理取消按钮
        handleCancel() {
            if (this.canClose) {
                setIgnoredToday(this.productType)
                this.dialogVisible = false
                this.resolvePromise?.(true)
            }
        },

        // 静默关闭弹框（不记录到localStorage）
        // 返回 false 阻止调用方继续执行后续代码
        closeDialogSilently() {
            if (this.dialogVisible) {
                this.dialogVisible = false
                this.resolvePromise?.(false)
                this.resetForm()
            }
        },

        // 处理确定按钮（第一个弹框）
        async handleConfirm() {
            if (!this.isSupPcAdmin) {
                showMessage.warning('只有超管才能操作')
                return
            }

            if (!this.form.name.trim()) {
                showMessage.warning('请输入授权代表姓名')
                return
            }

            if (!this.form.phone.trim()) {
                showMessage.warning('请输入授权代表手机号')
                return
            }

            // 手机号格式校验
            const phoneReg = /^1[3-9]\d{9}$/
            if (!phoneReg.test(this.form.phone)) {
                showMessage.warning('请输入正确的手机号')
                return
            }

            try {
                // 调用填写签署人信息接口
                await fillSigners({
                    name: this.form.name,
                    phone: this.form.phone,
                    productType: this.productType
                })

                // 调用查询签署信息接口
                const res: QueryUnsignedContractResDto = await queryUnsignedContract({
                    productType: this.productType
                })

                if (res.hasUnsigned === 1) {
                    // 有待签署合同，显示第二个弹框
                    this.currentStep = 2
                    this.days = res.days ?? 0
                    this.signerName = res.signer ?? ''
                    this.signUrl = res.url ?? ''
                    this.fileName = res.fileName ?? '保险承诺函'
                } else {
                    // 没有待签署合同，关闭弹框
                    this.dialogVisible = false
                    this.resolvePromise?.(true)
                }
            } catch (error) {
                console.error('填写签署人信息失败:', error)
            }
        },

        // 处理刷新按钮
        async handleRefresh() {
            const now = Date.now()
            if (now - this.lastRefreshTime < REFRESH_INTERVAL) {
                showMessage.warning('点击太频繁了，请5秒之后再试')
                return
            }

            this.lastRefreshTime = now

            try {
                const res: QueryUnsignedContractResDto = await queryUnsignedContract({
                    productType: this.productType
                })

                if (res.hasUnsigned === 0) {
                    // 已签署，关闭弹框
                    showMessage.success('签署成功')
                    this.dialogVisible = false
                    this.resolvePromise?.(true)
                } else {
                    // 未签署，更新数据
                    this.days = res.days ?? 0
                    this.signerName = res.signer ?? ''
                    this.signUrl = res.url ?? ''
                    this.fileName = res.fileName ?? '保险承诺函'
                    showMessage.info('合同尚未签署，请完成签署后再刷新')
                }
            } catch (error) {
                console.error('查询签署信息失败:', error)
            }
        },

        /**
         * 检查并显示弹框
         * @param type 产品类型
         * @returns Promise<boolean> true表示可以继续，false表示被阻断
         */
        checkAndShowDialog(type: number): Promise<boolean> {
            return new Promise(async (resolve) => {
                this.resolvePromise = resolve
                this.productType = type

                try {
                    // 查询是否需要收集签署人信息
                    const collectRes: QueryIsCollectHandlerInfoResDto = await queryIsCollectHandlerInfo({
                        productType: type
                    })

                    // 更新天数
                    this.days = collectRes.days ?? 0

                    // 需要收集签署人信息
                    if (collectRes.isCollect === 1) {
                        // 如果天数不为0，检查今天是否已忽略
                        if (this.days !== 0 && isIgnoredToday(type)) {
                            resolve(true)
                            return
                        }
                        this.resetForm()
                        this.currentStep = 1
                        this.dialogVisible = true
                        return
                    }

                    // 不需要收集，查询是否有待签署合同
                    const unsignedRes: QueryUnsignedContractResDto = await queryUnsignedContract({
                        productType: type
                    })

                    // 更新天数
                    this.days = unsignedRes.days ?? 0

                    // 如果天数不为0，检查今天是否已忽略
                    if (this.days !== 0 && isIgnoredToday(type)) {
                        resolve(true)
                        return
                    }

                    if (unsignedRes.hasUnsigned === 1) {
                        // 有待签署合同，显示第二个弹框
                        this.currentStep = 2
                        this.signerName = unsignedRes.signer ?? ''
                        this.signUrl = unsignedRes.url ?? ''
                        this.fileName = unsignedRes.fileName ?? '保险承诺函'
                        this.dialogVisible = true
                        return
                    }

                    // 不需要弹框
                    resolve(true)
                } catch (error) {
                    console.error('检查签署合同失败:', error)
                    // 出错时不阻断流程
                    resolve(true)
                }
            })
        }
    }
})
